"use client";

import { motion, AnimatePresence } from "framer-motion";
import {
  X,
  Calendar,
  Clock,
  Users,
  MapPin,
  Tag,
  Building2,
} from "lucide-react";
import { Event } from "@/app/services/eventService";
import Button from "@/components/ui/Button";
import { fadeInUp } from "@/lib/animations";

interface EventDetailsModalProps {
  event: Event | null;
  isOpen: boolean;
  onClose: () => void;
}

export default function EventDetailsModal({
  event,
  isOpen,
  onClose,
}: EventDetailsModalProps) {
  if (!event) return null;

  // Format date
  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString("en-US", {
      weekday: "long",
      month: "long",
      day: "numeric",
      year: "numeric",
    });
  };

  // Format time
  const formatTime = (timeStr: string) => {
    const [hours, minutes] = timeStr.split(":");
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? "PM" : "AM";
    const displayHour = hour % 12 || 12;
    return `${displayHour}:${minutes} ${ampm}`;
  };

  // Get event type color
  const getEventTypeColor = (type: string) => {
    const colors: Record<string, string> = {
      Conference: "from-blue-500/20 to-blue-600/10",
      Workshop: "from-purple-500/20 to-purple-600/10",
      "Product Launch": "from-pink-500/20 to-pink-600/10",
      Exhibition: "from-yellow-500/20 to-yellow-600/10",
      "Corporate Meeting": "from-green-500/20 to-green-600/10",
      "Private Event": "from-indigo-500/20 to-indigo-600/10",
      "Government Event": "from-red-500/20 to-red-600/10",
      "Internal Company Event": "from-teal-500/20 to-teal-600/10",
      // Backend event types
      INTERNAL: "from-green-500/20 to-green-600/10",
      EXTERNAL: "from-blue-500/20 to-blue-600/10",
    };
    return colors[type] || "from-primary/20 to-primary/10";
  };

  // Get event type badge color
  const getEventTypeBadgeColor = (type: string) => {
    const colors: Record<string, string> = {
      Conference: "bg-blue-500/10 text-blue-600 dark:text-blue-400",
      Workshop: "bg-purple-500/10 text-purple-600 dark:text-purple-400",
      "Product Launch": "bg-pink-500/10 text-pink-600 dark:text-pink-400",
      Exhibition: "bg-yellow-500/10 text-yellow-600 dark:text-yellow-400",
      "Corporate Meeting": "bg-green-500/10 text-green-600 dark:text-green-400",
      "Private Event": "bg-indigo-500/10 text-indigo-600 dark:text-indigo-400",
      "Government Event": "bg-red-500/10 text-red-600 dark:text-red-400",
      "Internal Company Event":
        "bg-teal-500/10 text-teal-600 dark:text-teal-400",
      // Backend event types
      INTERNAL: "bg-green-500/10 text-green-600 dark:text-green-400",
      EXTERNAL: "bg-blue-500/10 text-blue-600 dark:text-blue-400",
    };
    return colors[type] || "bg-primary/10 text-primary";
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-[10001] flex items-end sm:items-center justify-center">
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            onClick={onClose}
          />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, y: "100%", scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: "100%", scale: 0.95 }}
            transition={{ type: "spring", damping: 25, stiffness: 300 }}
            className="relative z-10 w-full sm:max-w-2xl mx-0 sm:mx-4"
          >
            <div className="bg-background sm:bg-card rounded-t-3xl sm:rounded-2xl shadow-2xl border-t sm:border border-border overflow-hidden max-h-[90vh] sm:max-h-[85vh] overflow-y-auto">
              {/* Header with gradient */}
              <div
                className={`relative bg-gradient-to-br ${getEventTypeColor(event.event_type)} p-6 sm:p-8`}
              >
                {/* Animated Background Effect */}
                <motion.div
                  className="absolute inset-0 bg-[radial-gradient(circle_at_30%_50%,rgba(124,179,66,0.15),transparent_70%)]"
                  animate={{
                    opacity: [0.3, 0.5, 0.3],
                  }}
                  transition={{
                    duration: 3,
                    repeat: Infinity,
                    ease: "easeInOut",
                  }}
                />

                {/* Close Button */}
                <button
                  onClick={onClose}
                  className="absolute cursor-pointer top-4 right-4 p-2 bg-card/80 hover:bg-card backdrop-blur-sm rounded-xl transition-colors z-10"
                >
                  <X className="w-5 h-5 text-heading" />
                </button>

                <div className="relative z-10">
                  {/* Badges */}
                  <div className="flex flex-wrap items-center gap-2 mb-4">
                    <span
                      className={`px-3 py-1.5 rounded-full text-xs font-semibold backdrop-blur-sm ${getEventTypeBadgeColor(event.event_type)}`}
                    >
                      {event.event_type.replace(/_/g, " ")}
                    </span>
                    <span className="px-3 py-1.5 bg-primary/10 text-primary rounded-full text-xs font-semibold backdrop-blur-sm">
                      {event.audience_type}
                    </span>
                  </div>

                  {/* Title */}
                  <h2 className="text-heading text-2xl sm:text-3xl font-bold leading-tight">
                    {event.event_name}
                  </h2>
                </div>
              </div>

              {/* Content */}
              <div className="p-6 sm:p-8 space-y-5">
                {/* Date */}
                <div className="flex items-start gap-4">
                  <div className="w-11 h-11 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <Calendar className="w-5 h-5 text-primary" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-heading font-semibold mb-1.5 text-sm">
                      Date
                    </h3>
                    <p className="text-text text-sm leading-relaxed">
                      {formatDate(event.event_date_start)}
                      {event.event_date_start !== event.event_date_end && (
                        <>
                          <br />
                          <span className="text-text/70">to </span>
                          {formatDate(event.event_date_end)}
                        </>
                      )}
                    </p>
                  </div>
                </div>

                {/* Time */}
                <div className="flex items-start gap-4">
                  <div className="w-11 h-11 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <Clock className="w-5 h-5 text-primary" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-heading font-semibold mb-1.5 text-sm">
                      Time
                    </h3>
                    <p className="text-text text-sm">
                      {formatTime(event.event_duration_from)} -{" "}
                      {formatTime(event.event_duration_to)}
                    </p>
                  </div>
                </div>

                {/* Location */}
                <div className="flex items-start gap-4">
                  <div className="w-11 h-11 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-5 h-5 text-primary" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-heading font-semibold mb-1.5 text-sm">
                      Location
                    </h3>
                    <p className="text-text text-sm leading-relaxed">
                      <span className="font-medium">
                        {event.location?.type === "internal"
                          ? event.location.room_name || "idworks Hub"
                          : event.location?.address || "idworks Hub"}
                      </span>
                      <br />
                      <span className="text-text/70">
                        KAFD, Riyadh, Saudi Arabia
                      </span>
                    </p>
                  </div>
                </div>

                {/* Attendees */}
                <div className="flex items-start gap-4">
                  <div className="w-11 h-11 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <Users className="w-5 h-5 text-primary" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-heading font-semibold mb-1.5 text-sm">
                      Expected Attendees
                    </h3>
                    <p className="text-text text-sm">
                      <span className="font-semibold text-primary">
                        {event.expected_attendees}
                      </span>{" "}
                      people
                    </p>
                  </div>
                </div>

                {/* Audience Type */}
                <div className="flex items-start gap-4">
                  <div className="w-11 h-11 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <Tag className="w-5 h-5 text-primary" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-heading font-semibold mb-1.5 text-sm">
                      Audience Type
                    </h3>
                    <p className="text-text text-sm capitalize">
                      {event.audience_type.toLowerCase()}
                    </p>
                  </div>
                </div>

                {/* Description (if available) */}
                {event.description && (
                  <div className="flex items-start gap-4">
                    <div className="w-11 h-11 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <Building2 className="w-5 h-5 text-primary" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-heading font-semibold mb-1.5 text-sm">
                        Description
                      </h3>
                      <div
                        className="text-text text-sm prose prose-sm dark:prose-invert max-w-none"
                        dangerouslySetInnerHTML={{ __html: event.description }}
                      />
                    </div>
                  </div>
                )}

                {/* Action Button */}
                <div className="pt-4">
                  <Button
                    variant="primary"
                    size="lg"
                    className="w-full"
                    onClick={onClose}
                  >
                    Got it
                  </Button>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}
